import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, Timestamp, query, orderBy } from 'firebase/firestore';
import PortalHeader from '../../components/Portal/PortalHeader';
import PsModal from '../../components/Portal/PsModal';
import TestModeBanner from '../../components/TestMode/TestModeBanner';
import { useAuth } from '../../contexts/AuthContext/AuthContext';
import { isAdminRole } from '../../utils/permissions';
import './ManageAnnouncements.css';

const ManageAnnouncements = () => {
	const { userProfile } = useAuth();
	// announcements delete is admin-only (firestore.rules); mirror it so a
	// teacher doesn't see a Delete button that fails at the server (#25).
	const isAdmin = isAdminRole(userProfile);
	const [announcements, setAnnouncements] = useState([]);
	const [loading, setLoading] = useState(true);
	const [showEditor, setShowEditor] = useState(false);
	const [editingAnnouncement, setEditingAnnouncement] = useState(null);

	// Form state
	const [title, setTitle] = useState('');
	const [message, setMessage] = useState('');
	const [type, setType] = useState('info');
	const [priority, setPriority] = useState(1);
	const [active, setActive] = useState(true);
	const [dismissible, setDismissible] = useState(true);
	const [showExpiration, setShowExpiration] = useState(false);
	const [expirationDays, setExpirationDays] = useState(7);

	useEffect(() => {
		fetchAnnouncements();
	}, []);

	const fetchAnnouncements = async () => {
		try {
			const q = query(collection(db, 'announcements'), orderBy('createdAt', 'desc'));
			const snapshot = await getDocs(q);
			const announcementsData = snapshot.docs.map(doc => ({
				id: doc.id,
				...doc.data()
			}));
			setAnnouncements(announcementsData);
		} catch (error) {
			console.error('Error fetching announcements:', error);
		} finally {
			setLoading(false);
		}
	};

	const handleCreateNew = () => {
		setEditingAnnouncement(null);
		setTitle('');
		setMessage('');
		setType('info');
		setPriority(1);
		setActive(true);
		setDismissible(true);
		setShowExpiration(false);
		setExpirationDays(7);
		setShowEditor(true);
	};

	const handleEdit = (announcement) => {
		setEditingAnnouncement(announcement);
		setTitle(announcement.title || '');
		setMessage(announcement.message);
		setType(announcement.type);
		setPriority(announcement.priority || 1);
		setActive(announcement.active);
		setDismissible(announcement.dismissible !== false);
		setShowExpiration(announcement.showExpiration || false);

		// Calculate days until expiration
		if (announcement.expiresAt) {
			const now = new Date();
			const expiresDate = announcement.expiresAt.toDate();
			const days = Math.ceil((expiresDate - now) / (1000 * 60 * 60 * 24));
			setExpirationDays(days > 0 ? days : 7);
		} else {
			setExpirationDays(7);
		}

		setShowEditor(true);
	};

	const handleSave = async () => {
		try {
			// Calculate expiration date
			const expiresAt = new Date();
			expiresAt.setDate(expiresAt.getDate() + parseInt(expirationDays));

			const announcementData = {
				title: title.trim() || null,
				message: message.trim(),
				type,
				priority: parseInt(priority),
				active,
				dismissible,
				showExpiration,
				expiresAt: Timestamp.fromDate(expiresAt),
				updatedAt: Timestamp.now(),
			};

			if (editingAnnouncement) {
				// Update existing announcement
				await updateDoc(doc(db, 'announcements', editingAnnouncement.id), announcementData);
				alert('Announcement updated successfully!');
			} else {
				// Create new announcement
				announcementData.createdAt = Timestamp.now();
				await addDoc(collection(db, 'announcements'), announcementData);
				alert('Announcement created successfully!');
			}

			setShowEditor(false);
			fetchAnnouncements();
		} catch (error) {
			console.error('Error saving announcement:', error);
			alert('Error saving announcement. Please try again.');
		}
	};

	const handleDelete = async (announcementId, announcementTitle) => {
		if (!window.confirm(`Are you sure you want to delete "${announcementTitle || 'this announcement'}"?`)) return;

		try {
			await deleteDoc(doc(db, 'announcements', announcementId));
			alert('Announcement deleted successfully!');
			fetchAnnouncements();
		} catch (error) {
			console.error('Error deleting announcement:', error);
			alert('Error deleting announcement. Please try again.');
		}
	};

	const toggleActive = async (announcement) => {
		try {
			await updateDoc(doc(db, 'announcements', announcement.id), {
				active: !announcement.active,
				updatedAt: Timestamp.now()
			});
			fetchAnnouncements();
		} catch (error) {
			console.error('Error toggling announcement status:', error);
			alert('Error updating announcement. Please try again.');
		}
	};

	const getTypeBadge = (type) => {
		const config = {
			urgent: { tone: 'danger', icon: 'fa-exclamation-triangle' },
			warning: { tone: 'warning', icon: 'fa-exclamation-circle' },
			success: { tone: 'success', icon: 'fa-check-circle' },
			info: { tone: 'info', icon: 'fa-info-circle' }
		};
		return config[type] || config.info;
	};

	if (loading) {
		return (
			<>
				<PortalHeader title='Manage Announcements' subtitle='Loading announcements...' />
				<TestModeBanner />
				<div className='ps-page'>
					<div className='ps-spinner' role='status' aria-label='Loading' />
				</div>
			</>
		);
	}

	return (
		<>
			<PortalHeader title='Manage Announcements' subtitle='Create and manage customer portal announcements' />
			<TestModeBanner />
			<div className='ps-page'>
				<div className='ma-header'>
					<div className='ma-counts'>
						<span className='ps-badge ps-badge--neutral'>{announcements.length} Announcements</span>
						<span className='ps-badge ps-badge--success'>
							{announcements.filter(a => a.active).length} Active
						</span>
						<span className='ps-badge ps-badge--neutral'>
							{announcements.filter(a => !a.active).length} Inactive
						</span>
					</div>
					<button type='button' className='ps-btn ps-btn--primary' onClick={handleCreateNew}>
						<i className='fa-sharp fa-solid fa-plus'></i>
						Create Announcement
					</button>
				</div>

				{announcements.length === 0 ? (
					<div className='ps-alert ps-alert--info'>
						<strong>No Announcements Yet</strong>
						<p>You haven't created any announcements yet.</p>
						<p className='ma-alert__last'>Click "Create Announcement" to post a message on the customer portal.</p>
					</div>
				) : (
					<div className='ps-table-wrap'>
						{/* ps-table--fit: the sidebar can shrink the content column below the
						    table's natural width, and the base ps-table only scrolls — which put
						    the Actions column (and its admin Delete) off-screen with no cue.
						    Fixed layout + per-column shares (ManageAnnouncements.css) keep every
						    column on screen in every sidebar state. */}
						<table className='ps-table ma-table ps-table--fit'>
							<thead>
								<tr>
									<th className='col--status'>Status</th>
									<th className='col--type'>Type</th>
									<th className='col--message'>Title/Message</th>
									<th className='col--expires'>Expires</th>
									<th className='col--priority'>Priority</th>
									<th className='col--actions'>Actions</th>
								</tr>
							</thead>
							<tbody>
								{announcements.map((announcement) => {
									const badge = getTypeBadge(announcement.type);
									const isExpired = announcement.expiresAt && announcement.expiresAt.toDate() < new Date();

									return (
										<tr key={announcement.id} className={isExpired ? 'ma-row--expired' : ''}>
											<td className='col--status'>
												<button
													type='button'
													className={`ps-btn${announcement.active ? ' ps-btn--success' : ''}`}
													onClick={() => toggleActive(announcement)}
												>
													{announcement.active ? 'Active' : 'Inactive'}
												</button>
												{isExpired && (
													<span className='ps-badge ps-badge--danger ma-expired'>Expired</span>
												)}
											</td>
											<td className='col--type'>
												<span className={`ps-badge ps-badge--${badge.tone}`}>
													<i className={`fa-sharp fa-solid ${badge.icon}`}></i>
													{announcement.type}
												</span>
											</td>
											<td className='col--message'>
												{announcement.title && (
													<strong className='d-block ma-clip'>{announcement.title}</strong>
												)}
												<div className='ma-preview ma-clip'>
													{announcement.message.substring(0, 100)}
													{announcement.message.length > 100 && '...'}
												</div>
											</td>
											<td className='ma-expires col--expires'>
												{announcement.expiresAt?.toDate().toLocaleDateString()}
											</td>
											<td className='col--priority'>
												<span className='ps-badge ps-badge--neutral'>{announcement.priority}</span>
											</td>
											<td className='col--actions'>
												<div className='ma-actions'>
													<button
														type='button'
														className='ps-btn-icon'
														aria-label='Edit announcement'
														onClick={() => handleEdit(announcement)}
													>
														<i className='fa-sharp fa-solid fa-edit'></i>
													</button>
													{isAdmin && (
														<button
															type='button'
															className='ps-btn-icon'
															aria-label='Delete announcement'
															onClick={() => handleDelete(announcement.id, announcement.title)}
														>
															<i className='fa-sharp fa-solid fa-trash'></i>
														</button>
													)}
												</div>
											</td>
										</tr>
									);
								})}
							</tbody>
						</table>
					</div>
				)}

				{/* Editor Modal — PsModal owns overlay, ESC, backdrop close and dialog
				    semantics; see its header for the focus-trap gap it does not close. */}
				<PsModal
					open={showEditor}
					onClose={() => setShowEditor(false)}
					title={editingAnnouncement ? 'Edit Announcement' : 'Create New Announcement'}
					titleIcon='fa-bullhorn'
					className='ma-modal'
					footer={
						<>
							<button type='button' className='ps-btn' onClick={() => setShowEditor(false)}>
								Cancel
							</button>
							<button
								type='button'
								className='ps-btn ps-btn--primary'
								onClick={handleSave}
								disabled={!message.trim()}>
								<i className='fa-sharp fa-solid fa-save'></i>
								{editingAnnouncement ? 'Update' : 'Create'} Announcement
							</button>
						</>
					}>
							<div className='ps-field'>
								<label className='ps-label' htmlFor='ma-title'>
									<strong>Title</strong> (Optional)
								</label>
								<input
									id='ma-title'
									type='text'
									className='ps-input'
									value={title}
									onChange={(e) => setTitle(e.target.value)}
									placeholder='e.g., Studio Closed This Week'
								/>
								<small className='ps-help'>Leave blank for no title</small>
							</div>

							<div className='ps-field'>
								<label className='ps-label' htmlFor='ma-message'>
									<strong>Message</strong>
								</label>
								<textarea
									id='ma-message'
									className='ps-textarea'
									rows={4}
									value={message}
									onChange={(e) => setMessage(e.target.value)}
									placeholder='Enter your announcement message...'
									required
								/>
							</div>

							<div className='ma-modal__row'>
								<div className='ps-field'>
									<label className='ps-label' htmlFor='ma-type'>
										<strong>Type</strong>
									</label>
									<select
										id='ma-type'
										className='ps-select'
										value={type}
										onChange={(e) => setType(e.target.value)}>
										<option value='info'>Info (Blue)</option>
										<option value='success'>Success (Green)</option>
										<option value='warning'>Warning (Yellow)</option>
										<option value='urgent'>Urgent (Red)</option>
									</select>
								</div>
								<div className='ps-field'>
									<label className='ps-label' htmlFor='ma-priority'>
										<strong>Priority</strong>
									</label>
									<input
										id='ma-priority'
										type='number'
										className='ps-input'
										min='1'
										max='10'
										value={priority}
										onChange={(e) => setPriority(e.target.value)}
									/>
									<small className='ps-help'>Higher priority shows first (1-10)</small>
								</div>
							</div>

							<div className='ps-field'>
								<label className='ps-label' htmlFor='ma-expires'>
									<strong>Expires In (Days)</strong>
								</label>
								<input
									id='ma-expires'
									type='number'
									className='ps-input'
									min='1'
									max='365'
									value={expirationDays}
									onChange={(e) => setExpirationDays(e.target.value)}
								/>
								<small className='ps-help'>
									Announcement will automatically hide after this many days
								</small>
							</div>

							<label className='ps-check'>
								<input
									type='checkbox'
									checked={active}
									onChange={(e) => setActive(e.target.checked)}
								/>
								Active (visible to customers)
							</label>

							<label className='ps-check'>
								<input
									type='checkbox'
									checked={dismissible}
									onChange={(e) => setDismissible(e.target.checked)}
								/>
								Allow customers to dismiss this announcement
							</label>

							<label className='ps-check'>
								<input
									type='checkbox'
									checked={showExpiration}
									onChange={(e) => setShowExpiration(e.target.checked)}
								/>
								Show expiration date to customers
							</label>

				</PsModal>
			</div>
		</>
	);
};

export default ManageAnnouncements;
